import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft, Calendar, FileText, GraduationCap, User, BookOpen, Users, Award } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';
import { toast } from 'react-toastify';

// Composants utilisés
import BulletinHeader from '../components/bulletin/BulletinHeader';
import StudentInfo from '../components/bulletin/StudentInfo';
import GradesTable from '../components/bulletin/GradesTable';
import ClassStatistics from '../components/bulletin/ClassStatistics';
import BulletinFooter from '../components/bulletin/BulletinFooter';
import PrintBulletinHeader from '../components/bulletin/PrintBulletinHeader';
import PrintGradesTable from '../components/bulletin/PrintGradesTable';
import PrintStudentInfo from '../components/bulletin/PrintStudentInfo';
import PrintClassStatistics from '../components/bulletin/PrintClassStatistics';
import PrintDecisionConseil from '../components/bulletin/PrintDecisionConseil';
import DecisionConseil from '../components/bulletin/DecisionConseil';
import PrintQRCode from '../components/bulletin/PrintQRCode';

const BulletinViewPage = () => {
  const { bulletinId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [loading, setLoading] = useState(true);
  const [bulletin, setBulletin] = useState(null);
  const [etablissement, setEtablissement] = useState(null);
  const [tableauHonneur, setTableauHonneur] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bulletinResponse, etablissementResponse] = await Promise.all([
          api.get(`/note/bulletins/${bulletinId}/`),
          api.get('etablissement/etablissement-infos/')
        ]);
        
        if (bulletinResponse.data.success) {
          const bulletinData = bulletinResponse.data.data;
          setBulletin(bulletinData);
        } else {
          throw new Error('Erreur lors du chargement du bulletin');
        }
        
        setEtablissement(etablissementResponse.data);
        
        // Charger le tableau d'honneur
        await fetchTableauHonneur();
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bulletinId]);

  const fetchTableauHonneur = async () => {
    try {
      const response = await api.get(`/note/tableau-honneur/${bulletinId}/`);
      if (response.data.success && response.data.data) {
        setTableauHonneur(response.data.data);
      } else {
        setTableauHonneur(null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du tableau d\'honneur:', error);
      setTableauHonneur(null);
    }
  };

  const handleTableauHonneurUpdate = (nouveauTableau) => {
    setTableauHonneur(nouveauTableau);
  };

  const handlePrint = () => {
    const printWindow = window.open('', 'PRINT');
    const content = printRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Bulletin de notes</title>
          <style>
            @page { 
              size: A4; 
              margin: 8mm; 
              padding: 0;
            }
            * { 
              margin: 0; 
              padding: 0; 
              box-sizing: border-box; 
            }
            body { 
              font-family: Arial, sans-serif; 
              font-size: 9pt; 
              line-height: 1.1; 
              color: #000; 
              background: white;
              max-height: 297mm;
              overflow: hidden;
            }
            table { 
              border-collapse: collapse; 
              width: 100%; 
              font-size: 7pt;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 2px 3px; 
              vertical-align: top; 
              font-size: 7pt;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              font-size: 7pt;
            }
            .no-border { border: none; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .bold { font-weight: bold; }
            .hidden { display: none !important; }
            .print\\:block { display: block !important; }
            .print\\:hidden { display: none !important; }
            
            /* Optimisations pour une seule page */
            .header-section { margin-bottom: 6px; }
            .student-info { margin-bottom: 4px; }
            .grades-table { margin-bottom: 4px; }
            .statistics-section { margin-bottom: 4px; }
            .footer-section { margin-top: 3px; }
            
            /* Réduction des espacements */
            h1, h2, h3 { margin: 1px 0; }
            p { margin: 0.5px 0; }
            div { margin: 0.5px 0; }
            
            /* Compression des tableaux */
            .compact-table th, .compact-table td {
              padding: 1px 2px;
              font-size: 6pt;
            }
            
            @media print {
              body { 
                margin: 0; 
                background: white;
                max-height: 297mm;
                overflow: hidden;
              }
              .print\\:block { display: block !important; }
              .print\\:hidden { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${content}
          <script>
            window.onload = function() {
              setTimeout(() => { window.print(); window.close(); }, 100);
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#202654] mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700">Chargement du bulletin...</h2>
            <p className="text-gray-500 mt-2">Veuillez patienter</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!bulletin || !etablissement) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Bulletin non trouvé</h1>
            <p className="text-gray-600 mb-6">Le bulletin que vous recherchez n'existe pas ou a été supprimé.</p>
            <button
              onClick={() => navigate('/bulletins-notes')}
              className="inline-flex items-center px-6 py-3 bg-[#202654] text-white font-medium rounded-lg hover:bg-[#202654]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour à la liste
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 py-8">
          <div id="bulletin-details" className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            
            {/* En-tête d'écran amélioré */}
            <div className="bg-gradient-to-r from-[#202654] via-[#2a2f6c] to-[#1e1b4b] p-8">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => navigate('/bulletins-notes')}
                    className="text-white hover:bg-white/10 p-3 rounded-xl transition-all duration-200 hover:scale-105"
                    aria-label="Retour à la liste"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>
                  <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Détails du Bulletin</h1>
                    <p className="text-white/80 text-lg">Consultation et impression</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/20">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-5 w-5 text-white/90" />
                      <span className="text-white/90 font-medium">
                        {bulletin.annee_academique?.libelle || 'Année académique'}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-6 py-3 bg-white text-[#202654] font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    aria-label="Imprimer le bulletin"
                  >
                    <Printer className="h-5 w-5 mr-2" />
                    Imprimer le Bulletin
                  </button>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-8">
              
              {/* En-tête du bulletin pour l'écran */}
              <div className="mb-10">
                <BulletinHeader 
                  etablissement={etablissement}
                  anneeAcademique={bulletin.annee_academique}
                  semestre={bulletin.semestre}
                />
              </div>

              {/* Informations de l'étudiant et de la classe */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-10">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <User className="h-6 w-6 mr-3 text-blue-600" />
                    Informations de l'étudiant
                  </h3>
                  <StudentInfo 
                    etudiant={bulletin.etudiant}
                    classe={bulletin.classe}
                    bulletin={bulletin}
                  />
                </div>
                <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-2xl p-4 sm:p-6 lg:p-8 border border-green-200 shadow-lg">
                  {/* En-tête avec icône et titre */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6 lg:mb-8">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                      <GraduationCap className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 break-words">Informations de classe</h3>
                      <p className="text-green-600 font-medium text-sm sm:text-base">Détails académiques et administratifs</p>
                    </div>
                  </div>

                  {/* Informations organisées en cartes */}
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    <div className="bg-white rounded-xl p-4 sm:p-6 border border-green-100 shadow-sm">
                      <div className="flex items-center space-x-2 mb-3">
                        <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 font-semibold text-base sm:text-lg break-words">Classe:</span>
                      </div>
                      <p className="text-gray-900 text-lg sm:text-xl font-bold break-words">
                        {bulletin.classe?.libelle || 'N/A'}
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 sm:p-6 border border-green-100 shadow-sm">
                      <div className="flex items-center space-x-2 mb-3">
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 font-semibold text-base sm:text-lg break-words">Niveau:</span>
                      </div>
                      <p className="text-gray-900 text-lg sm:text-xl font-bold break-words">
                        {bulletin.classe?.niveau?.libelle || 
                         bulletin.informations_inscription?.niveau || 
                         'Non défini'}
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 sm:p-6 border border-green-100 shadow-sm">
                      <div className="flex items-center space-x-2 mb-3">
                        <Award className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 font-semibold text-base sm:text-lg break-words">Filière:</span>
                      </div>
                      <p className="text-gray-900 text-lg sm:text-xl font-bold break-words">
                        {bulletin.informations_inscription?.filiere || 'Non définie'}
                      </p>
                    </div>

                    <div className="bg-white rounded-xl p-4 sm:p-6 border border-green-100 shadow-sm">
                      <div className="flex items-center space-x-2 mb-3">
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 font-semibold text-base sm:text-lg break-words">Effectif:</span>
                      </div>
                      <p className="text-gray-900 text-lg sm:text-xl font-bold break-words">
                        {bulletin.moyenne_semestre?.effectif_classe || 'N/A'} étudiants
                      </p>
                    </div>
                  </div>

                  {/* Date d'édition en bas */}
                  <div className="mt-6 bg-white rounded-xl p-4 sm:p-6 border border-green-100 shadow-sm">
                    <div className="flex items-center space-x-2 mb-3">
                      <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 font-semibold text-base sm:text-lg break-words">Date d'édition:</span>
                    </div>
                    <p className="text-gray-900 text-lg sm:text-xl font-bold break-words">
                      {new Date(bulletin.date_generation).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tableau des notes par module */}
              <div className="mb-10">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <BookOpen className="h-6 w-6 mr-3 text-purple-600" />
                    Tableau des notes par module
                  </h3>
                  <GradesTable 
                    details={bulletin.details}
                    moyenneSemestre={bulletin.moyenne_semestre}
                  />
                </div>
              </div>

              {/* Statistiques de classe */}
              <div className="mb-10">
                <ClassStatistics 
                  moyenneSemestre={bulletin.moyenne_semestre}
                />
              </div>

              {/* Décision du conseil */}
              <div className="mb-10">
                <DecisionConseil 
                  bulletinId={bulletinId}
                  tableauHonneur={tableauHonneur}
                  onUpdate={handleTableauHonneurUpdate}
                />
              </div>

              {/* Pied de page */}
              <BulletinFooter 
                bulletin={bulletin}
              />

            </div>
          </div>

          {/* Contenu pour l'impression */}
          <div ref={printRef} className="hidden" style={{ position: 'relative' }}>
            <PrintBulletinHeader 
              etablissement={etablissement} 
              anneeAcademique={bulletin?.annee_academique}
              semestre={bulletin?.semestre}
            />
            <PrintStudentInfo 
              etudiant={bulletin?.etudiant} 
              classe={bulletin?.classe}
              bulletin={bulletin}
            />
            <PrintGradesTable 
              details={bulletin?.details} 
              moyenneSemestre={bulletin?.moyenne_semestre}
            />
            
            {/* Statistiques et Décision côte à côte */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '8px',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: '1', minWidth: '0' }}>
                <PrintClassStatistics 
                  moyenneSemestre={bulletin?.moyenne_semestre}
                />
              </div>
              <div style={{ 
                width: '1px', 
                backgroundColor: '#000', 
                height: '100%',
                margin: '0 4px'
              }}></div>
              <div style={{ flex: '1', minWidth: '0' }}>
                <PrintDecisionConseil 
                  tableauHonneur={tableauHonneur}
                />
              </div>
            </div>
            
            {/* QR Code avec informations clés */}
            <PrintQRCode 
              bulletin={bulletin}
              etablissement={etablissement}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BulletinViewPage;
